import React, { useCallback, useEffect, useState } from 'react'
import { Dropdown, DropdownMenu, DropdownToggle, Spinner } from 'reactstrap'
import { getRegs } from '../../utils/fc'
import { getOikosFileUrl } from '../../utils/linksOikos/link'
import currencyFormatter from '../../utils/currencyFormatter'
import { TP_DOC_REC_LIBERACAO_FATURAMENTO } from '../../constants/oikosReceitas'
import {
  fmtDateBr,
  isLiberadoParaEmissao,
  num,
  parseDate,
} from '../../pages/Financial/Receitas/ContasAReceber/utils'
import { startBadgePoll } from './headerBadgePoll'

const LIB_DOC_PATH_SQL = `(SELECT arquivo_caminho FROM arquivo WHERE arquivo.arquivo_tabRef = 'documento' AND arquivo.arquivo_tabId = (SELECT doc.documento_id FROM documento doc WHERE doc.tpDocumento_id = ${TP_DOC_REC_LIBERACAO_FATURAMENTO} AND doc.documento_id IN (SELECT documento_id FROM documentoVinc WHERE documentoVinc.documentoVinc_tabRef = 'rec' AND documentoVinc.documentoVinc_tabId = rec.rec_id ORDER BY documentoVinc_id DESC) ORDER BY doc.documento_id DESC LIMIT 1) ORDER BY arquivo_id DESC LIMIT 1)`

const REC_FIELDS = {
  '(SELECT IFNULL((SELECT proj_nome FROM proj WHERE proj.orcRubr_id = rec.orcRubr_id AND proj.proj_ativo = 1 LIMIT 1), \'SEM PROJETO\'))': 'recProjeto',
  '(SELECT IFNULL(NULLIF(TRIM(pj.pj_razaosocial), \'\'), pj.pj_nomefanta) FROM pj WHERE pj.pj_id = rec.pj_tomador LIMIT 1)': 'pjTomadorNome',
  [LIB_DOC_PATH_SQL]: 'arquivo_caminho_liberacao',
}

const sortLiberadas = (a, b) => {
  const da = parseDate(a.rec_dtNfPrev)?.getTime() ?? 0
  const db = parseDate(b.rec_dtNfPrev)?.getTime() ?? 0
  if (da !== db) return da - db
  return num(b.rec_vlrBru) - num(a.rec_vlrBru)
}

async function fetchNfLiberadas() {
  const recs = await getRegs('rec', { rec_aut: 1, rec_fat: 0 }, REC_FIELDS)
  return (recs || []).filter(isLiberadoParaEmissao).sort(sortLiberadas)
}

const NfIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
    <rect x="5" y="3" width="18" height="22" rx="3" fill="#fff" />
    <path d="M5 6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v5H5V6z" fill="#9FC74B" />
    <path d="M17 3h6l-6 6V3z" fill="#81A63C" />
    <text x="12.2" y="8.7" textAnchor="middle" fill="#081E38" fontSize="5.4" fontWeight="800" fontFamily="Inter, sans-serif">NF</text>
    <rect x="8" y="13.2" width="12" height="1.5" rx="0.75" fill="#CFCFCF" />
    <rect x="8" y="16.6" width="9" height="1.5" rx="0.75" fill="#CFCFCF" />
    <rect x="8" y="20" width="7" height="1.5" rx="0.75" fill="#CFCFCF" />
  </svg>
)

const InfoRow = ({ label, value }) => (
  <div className="header-nf-menu-row">
    <span>{label}</span>
    <strong>{value || '—'}</strong>
  </div>
)

export default function HeaderNfLiberadasMenu() {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])

  const loadItems = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const next = await fetchNfLiberadas()
      setItems(next)
    } catch (err) {
      console.log('Erro ao carregar NFs liberadas para emissão', err)
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => startBadgePoll(() => loadItems({ silent: true })), [loadItems])

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next) loadItems()
  }

  const count = items.length

  return (
    <Dropdown isOpen={open} toggle={toggle} className="d-flex align-items-center justify-content-center">
      <style>{`
        .header-nf-menu {
          width: 420px;
          max-width: min(420px, calc(100vw - 24px));
          padding: 0;
          border: 0;
          border-radius: 10px;
          box-shadow: 0 12px 32px rgba(8, 30, 56, 0.18);
          overflow: hidden;
          z-index: 1100;
        }
        .header-nf-menu-head {
          padding: 12px 14px 11px;
          background: #EEF3FB;
          color: #081E38;
          border-bottom: 2px solid #1C47A4;
        }
        .header-nf-menu-head h6 {
          margin: 0 0 2px;
          font-size: 15px;
          font-weight: 800;
          line-height: 1.3;
          color: #081E38;
          white-space: normal;
        }
        .header-nf-menu-head span {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: #3d4f6f;
          opacity: 1;
        }
        .header-nf-menu-list {
          max-height: 380px;
          overflow: auto;
          background: #fff;
        }
        .header-nf-menu-item {
          padding: 12px 14px;
          border-bottom: 1px solid #eceff3;
        }
        .header-nf-menu-item:last-child {
          border-bottom: 0;
        }
        .header-nf-menu-ep {
          margin: 0 0 6px;
          font-size: 13px;
          font-weight: 700;
          color: #081E38;
          line-height: 1.3;
        }
        .header-nf-menu-row {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          font-size: 12px;
          line-height: 1.45;
          color: #6c757d;
        }
        .header-nf-menu-row strong {
          color: #273351;
          font-weight: 600;
          text-align: right;
        }
        .header-nf-menu-empty,
        .header-nf-menu-loading {
          padding: 28px 16px;
          text-align: center;
          color: #6c757d;
          font-size: 13px;
        }
        .header-nf-evidencia {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-top: 8px;
          font-size: 12px;
          font-weight: 700;
          color: #1C47A4;
        }
        .header-nf-evidencia:hover {
          color: #1B448C;
          text-decoration: underline;
        }
        .header-nf-evidencia-missing {
          display: inline-block;
          margin-top: 8px;
          font-size: 12px;
          color: #9aa3ad;
        }
      `}</style>
      <DropdownToggle
        tag="button"
        type="button"
        className="d-flex align-items-center justify-content-center p-3 d-xs-none header-item font-size-22"
        style={{ background: 'none', border: 0, cursor: 'pointer' }}
        title="Notas fiscais liberadas para emissão"
        aria-label={
          count > 0
            ? `${count} notas fiscais liberadas para emissão`
            : 'Nenhuma nota fiscal liberada para emissão'
        }
      >
        <span className="header-nf-icon header-task-icon mr-2">
          <NfIcon />
          {count > 0 && (
            <span className="header-task-badge">
              {count > 99 ? '99+' : count}
            </span>
          )}
        </span>
      </DropdownToggle>
      <DropdownMenu right className="header-nf-menu">
        <div className="header-nf-menu-head">
          <h6>Notas liberadas para emissão</h6>
          <span>
            {loading ? 'Atualizando…' : `${count} ${count === 1 ? 'nota aguardando' : 'notas aguardando'}`}
          </span>
        </div>
        <div className="header-nf-menu-list">
          {loading && items.length === 0 && (
            <div className="header-nf-menu-loading">
              <Spinner size="sm" />
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="header-nf-menu-empty">Nenhuma nota fiscal liberada para emissão.</div>
          )}
          {items.map((rec) => {
            const evidenciaUrl = getOikosFileUrl(rec.arquivo_caminho_liberacao)
            const valor = rec.rec_vlrLibAutNf != null && rec.rec_vlrLibAutNf !== ''
              ? rec.rec_vlrLibAutNf
              : rec.rec_vlrBru
            return (
              <div key={rec.rec_id} className="header-nf-menu-item">
                <p className="header-nf-menu-ep">{rec.rec_ep || `Receita #${rec.rec_id}`}</p>
                <InfoRow label="Projeto" value={rec.recProjeto} />
                <InfoRow label="Tomador" value={rec.pjTomadorNome} />
                <InfoRow label="Valor" value={currencyFormatter.format(num(valor))} />
                <InfoRow label="Limite de faturamento" value={fmtDateBr(rec.rec_dtNfPrev)} />
                <InfoRow label="Liberação" value={fmtDateBr(rec.rec_dtLibAutNf)} />
                {evidenciaUrl ? (
                  <a
                    className="header-nf-evidencia"
                    href={evidenciaUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="ri-file-text-line" aria-hidden="true" />
                    Evidência de autorização
                  </a>
                ) : (
                  <span className="header-nf-evidencia-missing">Sem evidência de autorização</span>
                )}
              </div>
            )
          })}
        </div>
      </DropdownMenu>
    </Dropdown>
  )
}
